
import { Contractor } from "@/types/contractor";
import { getPlaceholderImage } from "@/utils/imageUtils";
import { ContractorBadge } from "./ContractorBadge";
import { ContractorHeader } from "./details/ContractorHeader";
import { ContractorRating } from "./details/ContractorRating";
import { ContractorContact } from "./details/ContractorContact";
import { ContractorImage } from "./details/ContractorImage";
import { ClaimBusinessButton } from "./claim/ClaimBusinessButton";

interface ContractorDetailsProps {
  name: string;
  serviceType: string;
  rating: number;
  reviewCount: number;
  address: string;
  phone: string;
  website?: string;
  pageUrl: string;
  viewMode?: "grid" | "list";
  featured?: boolean;
}

import { normalizeContractorName } from "@/utils/urlUtils";

export const ContractorDetails = ({ 
  name,
  serviceType,
  rating,
  reviewCount,
  address,
  phone,
  website,
  pageUrl,
  viewMode = "grid",
  featured = false
}: ContractorDetailsProps) => {
  // Normalize contractor name (e.g., rename "Edmond Tree Service" to "Redbud Tree Service")
  const displayName = normalizeContractorName(name);
  
  // Determine if link should be nofollow (all external links except exceptions)
  // Use original name for checking exceptions since that's what's in the database
  const shouldNofollow = () => {
    const nameLower = name.toLowerCase();
    // Exceptions: Edmond Window Cleaning, Edmond Tree Service (to be renamed to Redbud Tree Service), Evolve Agency
    const exceptions = [
      'edmond window cleaning',
      'edmond tree service',
      'redbud tree service',
      'evolve agency'
    ];
    return !exceptions.some(exception => nameLower.includes(exception));
  };

  const relAttribute = shouldNofollow() 
    ? "noopener noreferrer nofollow" 
    : "noopener noreferrer";

  return (
    <div className={`p-4 flex flex-col justify-between ${viewMode === "list" ? "flex-1" : ""}`}>
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
            <a href={pageUrl}>{displayName}</a>
          </h3>
          {featured && (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
              Featured
            </span>
          )}
        </div>
        
        <p className="text-sm text-muted-foreground">{serviceType}</p>
        
        <div className="flex items-center space-x-1">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className={`w-4 h-4 ${
                  i < Math.round(rating) ? "text-yellow-400" : "text-gray-300"
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
              </svg>
            ))}
          </div>
          <span className="text-sm">
            {rating.toFixed(1)} ({reviewCount} review{reviewCount !== 1 ? "s" : ""})
          </span>
        </div>
        
        <p className="text-sm truncate">{address}</p>
        
        <div className="flex flex-wrap gap-2 mt-4">
          <a
            href={`tel:${phone.replace(/\D/g, "")}`}
            className="inline-flex items-center space-x-1 text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
            </svg>
            <span>Call</span>
          </a>
          
          {website && (
            <a
              href={website.startsWith("http") ? website : `https://${website}`}
              target="_blank"
              rel={relAttribute}
              className="inline-flex items-center space-x-1 text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
              <span>Website</span>
            </a>
          )}
        </div>
      </div>
      
      <div className="mt-4">
        <a
          href={pageUrl}
          className="w-full text-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
        >
          View Details →
        </a>
      </div>
    </div>
  );
};
